import _ from 'lodash';
import {
  selectRandomElementFromArray,
  shuffleArray,
} from '../../../lib/utilityFunctions';
import { itemIsCorrect, retrieveAttributeList } from '../helpers/DataSetHelper';
/**
 * Definition of terms:
 * - available items:
 *    Items list from which user should select correct response
 * - grid items:
 *    items that is used to build the grid user should complete
 * - missing item placeholder
 *    object tat contains information about correct answer. Is used to define whether users answer right or wrong
 */

/*
 The goal ot this class it to provide data for game levels. It parses curriculum data and generates grid items for it
 according to current game level
  */
class CurriculumManager {
  constructor() {
    this.curriculum = {};
    this.currentLevel = 1;
    this.itemsSet = [];
    this.gridItems = [];
    this.availableItems = [];
    // contains 4 unique placeholders, so user can't select items of the same type in the row
    this.placeholdersShuffleBag = [];
    this.currentPlaceholder = {};
  }

  // --------------------------------------------------------------------------
  // PUBLIC
  //
  setCurriculum(jsonData) {
    this.curriculum = jsonData;
    this.populateData();
  }

  getGridItems() {
    return this.gridItems;
  }

  getAvailableItems() {
    return this.availableItems;
  }

  // simplifies available items grid
  getFewAvailableItems() {
    let attributes = retrieveAttributeList(this.getItemsSetType(this.currentLevel));
    attributes = shuffleArray(attributes);
    // simplified grid should have at least one matching item
    const correctItem = this.availableItems.find(item =>
      itemIsCorrect(
        this.getItemsSetType(this.currentLevel),
        this.getMissingItemPlaceholder(),
        item,
      ));

    const firstIncorrectItem = this.availableItems.find(item =>
      item[attributes[0]] === correctItem[attributes[0]] &&
      item[attributes[1]] !== correctItem[attributes[1]]);

    const secondIncorrectItem = this.availableItems.find(item =>
      item[attributes[0]] !== correctItem[attributes[0]] &&
      item[attributes[1]] === correctItem[attributes[1]]);

    const thirdIncorrectItem = this.availableItems.find(item =>
      item[attributes[0]] !== correctItem[attributes[0]] &&
      item[attributes[1]] !== correctItem[attributes[1]]);

    return shuffleArray([
      correctItem,
      firstIncorrectItem,
      secondIncorrectItem,
      thirdIncorrectItem,
    ]);
  }

  getMissingItemPlaceholder() {
    return this.currentPlaceholder;
  }

  populateData(level = 1) {
    this.loadItemsSet(level);
    this.initPlaceholderShuffleBag();
    this.setCurrentPlaceholder();
    this.loadGrids();
  }

  reloadGrids() {
    this.loadGrids();
  }

  nextStandardItem() {
    this.setCurrentPlaceholder();
    this.loadGrids();
  }

  loadItemsSet(level = 1) {
    this.currentLevel = level;
    this.itemsSet = this.curriculum[`level_${level}`].items.itemsList;
  }

  getItemsSetType(level = 1) {
    return this.curriculum[`level_${level}`].type;
  }

  getItemsSetId(level = 1) {
    return this.curriculum[`level_${level}`].setId;
  }

  getZapsEarned(level) {
    return this.curriculum[`level_${level}`].zapsEarned;
  }

  // --------------------------------------------------------------------------
  // PRIVATE
  //
  initPlaceholderShuffleBag() {
    const setType = this.getItemsSetType(this.currentLevel);
    switch (setType) {
      case 'picture_set':
        this.placeholdersShuffleBag = this.buildPicturesShuffleBag();
        break;
      case 'word_set':
        this.placeholdersShuffleBag = this.buildWordShuffleBag();
        break;
      default:
        this.placeholdersShuffleBag = [];
    }
  }

  buildPicturesShuffleBag() {
    const itemsSet = shuffleArray(this.itemsSet);
    const anyItem = selectRandomElementFromArray(itemsSet);

    const groupedByCategory = _.partition(
      itemsSet,
      item => item.category === anyItem.category,
    );
    const firstRow = groupedByCategory[0]
      .filter(item => item.color === anyItem.color)
      .slice(0, 2);
    const secondRow = groupedByCategory[0]
      .filter(item => item.color !== anyItem.color)
      .slice(0, 2);
    const thirdRow = groupedByCategory[1]
      .filter(item => item.color === anyItem.color)
      .slice(0, 2);
    const fourthRow = groupedByCategory[1]
      .filter(item => item.color !== anyItem.color)
      .slice(0, 2);

    return shuffleArray([
      ...firstRow,
      ...secondRow,
      ...thirdRow,
      ...fourthRow,
    ]).map(item => ({ ...item, content: '' }));
  }

  buildWordShuffleBag() {
    const itemsSet = shuffleArray(this.itemsSet);
    const anyItem = selectRandomElementFromArray(itemsSet);
    const groupedByFirstLetter = _.partition(
      itemsSet,
      item => item.first_letter === anyItem.first_letter,
    );
    const firstRow = groupedByFirstLetter[0]
      .filter(item => item.category === anyItem.category)
      .slice(0, 2);
    const secondRow = groupedByFirstLetter[0]
      .filter(item => item.category !== anyItem.category)
      .slice(0, 2);
    const thirdRow = groupedByFirstLetter[1]
      .filter(item => item.category === anyItem.category)
      .slice(0, 2);
    const fourthRow = groupedByFirstLetter[1]
      .filter(item => item.category !== anyItem.category)
      .slice(0, 2);

    return shuffleArray([
      ...firstRow,
      ...secondRow,
      ...thirdRow,
      ...fourthRow,
    ]).map(item => ({ ...item, content: '' }));
  }

  setCurrentPlaceholder() {
    if (this.placeholdersShuffleBag.length === 0) {
      this.initPlaceholderShuffleBag();
    }

    this.currentPlaceholder = this.placeholdersShuffleBag.pop();
  }

  loadPictureGrids() {
    const availableItems = shuffleArray(this.itemsSet.slice());

    const firstItem = availableItems.find(item =>
      item.category === this.currentPlaceholder.category &&
      item.color !== this.currentPlaceholder.color);
    this.gridItems.push(firstItem);
    const secondItem = availableItems.find(item =>
      item.category !== this.currentPlaceholder.category &&
      item.color === this.currentPlaceholder.color);
    this.gridItems.push(secondItem);

    const thirdItem = availableItems.find(item =>
      item.category !== this.currentPlaceholder.category &&
      item.color !== this.currentPlaceholder.color);
    this.gridItems.push(thirdItem);

    this.availableItems = _.difference(availableItems, this.gridItems);
  }

  loadWordGrids() {
    const availableItems = shuffleArray(this.itemsSet.slice());

    const firstItem = availableItems.find(item =>
      item.first_letter === this.currentPlaceholder.first_letter &&
      item.category !== this.currentPlaceholder.category);
    this.gridItems.push(firstItem);
    const secondItem = availableItems.find(item =>
      item.first_letter !== this.currentPlaceholder.first_letter &&
      item.category === this.currentPlaceholder.category);
    this.gridItems.push(secondItem);
    const thirdItem = availableItems.find(item =>
      item.first_letter !== this.currentPlaceholder.first_letter &&
      item.category !== this.currentPlaceholder.category);
    this.gridItems.push(thirdItem);

    this.availableItems = _.difference(availableItems, this.gridItems);
  }

  loadGrids() {
    this.gridItems = [];
    this.availableItems = [];

    const setType = this.getItemsSetType(this.currentLevel);
    switch (setType) {
      case 'picture_set':
        this.loadPictureGrids();
        break;
      case 'word_set':
        this.loadWordGrids();
        break;
      default:
        this.gridItems = [];
        this.availableItems = [];
    }
  }
}

const ManagerSingleton = new CurriculumManager();

export default ManagerSingleton;
